import React, {
  useContext,
  createContext,
  useReducer,
  useCallback,
} from "react";
import { HourlyDataReducer } from "../Reducers/HourlyDataReducer";
import {
  getCurrentBranchesObject,
  getDateFromWeekAndDay,
} from "../GlobalFunctions";
import { MenuItem, InputAdornment } from "@material-ui/core";
import { statuses } from "../Mappings";
import { roles } from "../Mappings";
import { UserDataContext } from "./UserDataContext";

export const HourlyDataContext = createContext();

const HourlyDataContextProvider = (props) => {
  const [hourlyData, dispatch] = useReducer(HourlyDataReducer, {});
  const { userData } = useContext(UserDataContext);

  console.log("Rerender");

  const createBranchModel = useCallback(
    (params) => {
      const branchesObject = getCurrentBranchesObject(
        userData.branches,
        params.week
      );

      return {
        assignedBranchId: {
          displayName: "Branch",
          displayValue: (value) => branchesObject[value].shortCode,
          validate: (value) => value !== "",
          textFieldProps: {
            name: "assignedBranchId",
            select: true,
            children: Object.keys(branchesObject).map((branchId) => (
              <MenuItem key={branchId} value={branchId}>
                {branchesObject[branchId].branchName}
              </MenuItem>
            )),
          },
        },
        assignedAmount: {
          displayName: "Amount",
          displayValue: (value) => Math.round(value * 100) / 100,
          validate: (value) => value !== "",
          textFieldProps: {
            name: "assignedAmount",
          },
        },
      };
    },
    [userData.branches]
  );

  const hoursDiff = (startTime, endTime) => {
    // get hours between start and end DateTimes
    const startDate = new Date(`1900-01-01T${endTime}:00`);
    const endDate = new Date(`1900-01-01T${startTime}:00`);
    // console.log((startDate - endDate) / 3600000);
    const totalHours = (startDate - endDate) / 3600000;
    return isNaN(totalHours) ? 0 : totalHours;
  };

  const createHourlyModel = useCallback((params) => {
    const { dateIsoString } = getDateFromWeekAndDay(params.week, params.day);
    const getIsoString = (time) =>
      dateIsoString.replace("00:00:00", `${time}:00`);

    return {
      startTime: {
        displayName: "Start Time",
        displayValue: (allValues) => allValues.startTime,
        validate: (allValues) =>
          allValues.startTime !== "" &&
          hoursDiff(allValues.startTime, allValues.endTime) > 0,
        mapToRequestValue: (allValues) =>
          getIsoString(allValues.startTimeString),
        // getValueFromRequest: (value) => value.match(/T(\d\d:\d\d):\d\d/)[1],
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "startTime",
          type: "time",
          InputLabelProps: {
            shrink: true,
          },
        },
      },

      endTime: {
        displayName: "End Time",
        displayValue: (allValues) => allValues.endTime,
        validate: (allValues) =>
          allValues.endTime !== "" &&
          hoursDiff(allValues.startTime, allValues.endTime) > 0,
        mapToRequestValue: (allValues) => getIsoString(allValues.endTimeString),
        // getValueFromRequest: (value) => value.match(/T(\d\d:\d\d):\d\d/)[1],
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "endTime",
          type: "time",
          InputLabelProps: {
            shrink: true,
          },
        },
      },

      startTimeString: {
        displayName: "Start Time",
        displayValue: (allValues) => allValues.startTimeString,
        validate: (allValues) =>
          allValues.startTimeString !== "" &&
          hoursDiff(allValues.startTimeString, allValues.endTimeString) > 0,
        mapToRequestValue: (allValues) =>
          getIsoString(allValues.startTimeString),
        // getValueFromRequest: (value) => value.match(/T(\d\d:\d\d):\d\d/)[1],
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "startTimeString",
          type: "time",
          InputLabelProps: {
            shrink: true,
          },
        },
      },

      endTimeString: {
        displayName: "End Time",
        displayValue: (allValues) => allValues.endTimeString,
        validate: (allValues) =>
          allValues.endTimeString !== "" &&
          hoursDiff(allValues.startTimeString, allValues.endTimeString) > 0,
        mapToRequestValue: (allValues) => getIsoString(allValues.endTimeString),
        // getValueFromRequest: (value) => value.match(/T(\d\d:\d\d):\d\d/)[1],
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "endTimeString",
          type: "time",
          InputLabelProps: {
            shrink: true,
          },
        },
      },

      hourlyRate: {
        displayName: "Hourly Rate",
        displayValue: (allValues) =>
          `$${(Math.round(allValues.hourlyRate * 100) / 100).toFixed(2)}`,
        validate: (allValues) =>
          /^-?\d+(\.\d{1,12})?$/.test(allValues.hourlyRate),
        mapToRequestValue: (allValues) => Number(allValues.hourlyRate),
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "hourlyRate",
          InputProps: { startAdornment: <InputAdornment>$</InputAdornment> },
        },
      },

      changedRateCons: {
        displayName: "Con-Notes",
        displayValue: (allValues) =>
          allValues.changedRateCons.map((con) => con.conNoteNum).join("\n"),
        validate: (allValues) => true,
        mapToRequestValue: (allValues) => allValues.changedRateCons || [],
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "changedRateCons",
          multiline: true,
          variant: "outlined",
          rowsMax: 3,
        },
      },

      totalHours: {
        displayName: "Total Hours",
        calculateValue: (allValues) => {
          try {
            return hoursDiff(
              allValues.startTimeString,
              allValues.endTimeString
            );
          } catch (error) {
            return 0;
          }
        },
        displayValue: (allValues) => {
          let hours;
          try {
            hours = hoursDiff(
              allValues.startTimeString,
              allValues.endTimeString
            );
          } catch (error) {
            hours = 0;
          }
          return Math.round(hours * 100) / 100;
        },
        validate: (allValues) => true,
        mapToRequestValue: (allValues) => Number(allValues.totalHours),
        textFieldProps: {
          name: "totalHours",
          disabled: true,
        },
      },

      totalCharge: {
        displayName: "Total Charge",
        calculateValue: (allValues) =>
          hoursDiff(allValues.startTimeString, allValues.endTimeString) *
          Number(allValues.hourlyRate),
        displayValue: (allValues) => {
          let hours;
          try {
            hours = hoursDiff(
              allValues.startTimeString,
              allValues.endTimeString
            );
          } catch (error) {
            hours = 0;
          }

          const charge = hours * allValues.hourlyRate;

          return `$${(Math.round(charge * 100) / 100).toFixed(2)}`;
        },
        validate: (allValues) => true,
        mapToRequestValue: (allValues) => {
          let hours;
          try {
            hours = hoursDiff(
              allValues.startTimeString,
              allValues.endTimeString
            );
          } catch (error) {
            hours = 0;
          }

          const charge = hours * allValues.hourlyRate;

          return Number((Math.round(charge * 100) / 100).toFixed(2));
        },
        textFieldProps: {
          name: "totalCharge",
          disabled: true,
        },
      },

      driverComments: {
        displayName: "Driver Comments",
        displayValue: (allValues) => allValues.driverComments,
        validate: (allValues) => true,
        mapToRequestValue: (allValues) => allValues.driverComments,
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "driverComments",
          placeholder: "Provide a brief description",
          multiline: true,
          variant: "outlined",
          rows: 3,
          rowsMax: 3,
        },
      },

      adminComments: {
        displayName: "Admin Comments",
        displayValue: (allValues) => allValues.adminComments,
        validate: (allValues) => true,
        mapToRequestValue: (allValues) => allValues.adminComments,
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "adminComments",
          placeholder: "Provide a brief description",
          multiline: true,
          variant: "outlined",
          rows: 3,
          rowsMax: 3,
        },
      },

      managerComments: {
        displayName: "Manager Comments",
        displayValue: (allValues) => allValues.managerComments,
        validate: (allValues) => true,
        mapToRequestValue: (allValues) => allValues.managerComments,
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "managerComments",
          placeholder: "Provide a brief description",
          multiline: true,
          variant: "outlined",
          rows: 3,
          rowsMax: 3,
        },
      },

      approvalState: {
        displayName: "Approval State",
        displayValue: (allValues) => statuses[allValues.approvalState],
        validate: (allValues) => true,
        mapToRequestValue: (allValues) => allValues.approvalState,
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "approvalState",
        },
      },

      changedRateAssignedBranches: {
        displayName: "Assigned Branches",
        displayValue: (allValues) =>
          allValues.changedRateAssignedBranches
            .map((assignedBranch) => assignedBranch.assignedBranchId)
            .join(", "),
        validate: (allValues) => {
          const totalCharge =
            hoursDiff(allValues.startTimeString, allValues.endTimeString) *
            Number(allValues.hourlyRate);

          const assignedBranchTotal = allValues.changedRateAssignedBranches.reduce(
            (acc, cur) => acc + cur.assignedAmount,
            0
          );

          console.log(assignedBranchTotal, totalCharge);

          return (
            allValues.changedRateAssignedBranches.length !== 0 &&
            Math.round(100 * totalCharge, 2) / 100 === assignedBranchTotal
          );
        },
        mapToRequestValue: (allValues) => allValues.changedRateAssignedBranches,
        getValueFromRequest: (value) => value,
      },
    };
  }, []);

  const comments = {
    [roles.tollAdmin]: "adminComments",
    [roles.ownerDriver]: "driverComments",
    // [roles.tollManager]: "managerComments",
  }[userData.roleId];

  const requiredColumns = {
    [roles.tollAdmin]: [
      "startTimeString",
      "endTimeString",
      "hourlyRate",
      "changedRateAssignedBranches",
    ],
    [roles.ownerDriver]: ["startTimeString", "endTimeString", "hourlyRate"],
  }[userData.roleId];

  const blankHourly = {
    [roles.tollAdmin]: {
      startTimeString: "",
      endTimeString: "",
      hourlyRate: "",
      changedRateCons: [],
      changedRateAssignedBranches: [],
      adminComments: "",
    },

    [roles.ownerDriver]: {
      startTimeString: "",
      endTimeString: "",
      hourlyRate: "",
      changedRateCons: [],
      driverComments: "",
    },
  }[userData.roleId];

  return (
    <HourlyDataContext.Provider
      value={{
        createHourlyModel,
        hourlyData,
        dispatch,
        comments,
        requiredColumns,
        blankHourly,
        createBranchModel,
      }}
    >
      {props.children}
    </HourlyDataContext.Provider>
  );
};

export default HourlyDataContextProvider;
